import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Lightbulb, Target, TrendingUp, Users, CheckCircle2, ArrowRight, Phone, Mail } from 'lucide-react';

const SAMIncubator = () => {
  const features = [
    {
      icon: <Target className="w-12 h-12 text-[#3498DB]" />,
      title: 'Business Strategy Development',
      description: 'Work with experienced consultants to develop a robust business strategy tailored to the UAE market.',
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-[#27AE60]" />,
      title: 'Market Analysis & Research',
      description: 'Comprehensive market research to identify opportunities and understand competitive landscape.',
    },
    {
      icon: <Lightbulb className="w-12 h-12 text-[#3498DB]" />,
      title: 'Step-by-Step Coaching',
      description: 'Personalized coaching sessions to guide you through every stage of your entrepreneurship journey.',
    },
    {
      icon: <Users className="w-12 h-12 text-[#27AE60]" />,
      title: 'Networking & Connections',
      description: 'Access to a network of investors, partners, and industry professionals in Dubai and UAE.',
    },
  ];

  const benefits = [
    'Tailored coaching support for your specific industry',
    'Access to experienced mentors and business advisors',
    'Structured project development framework',
    'Financial planning and funding guidance',
    'Legal and regulatory compliance support',
    'Marketing and branding strategy development',
    'Operational setup and process optimization',
    'Ongoing support throughout your growth journey',
  ];

  return (
    <div className="min-h-screen" data-testid="sam-incubator-page">
      {/* Hero Section */}
      <section
        className="relative h-[60vh] flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1600)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="incubator-hero"
      >
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block bg-[#3498DB]/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-[#5DADE2] font-semibold">SAM Incubator</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="incubator-hero-title">
              Transform Your <span className="text-[#5DADE2]">Business Idea</span> Into Reality
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
              Comprehensive support for talented entrepreneurs ready to structure and launch their business in the UAE
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="overview-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-semibold mb-6 text-[#2C3E50]">
                What is SAM Incubator?
              </h2>
              <div className="gold-line mb-6" />
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  SAM Incubator is a specialized program designed for talented and experienced entrepreneurs who need structured support to transform their business concepts into successful ventures in Dubai and the UAE.
                </p>
                <p>
                  We provide end-to-end coaching, from initial concept validation to market entry and growth, ensuring you have all the tools, knowledge, and connections needed to succeed.
                </p>
                <p>
                  Whether you're launching a startup, expanding internationally, or pivoting your existing business, our incubator provides the framework and support system to accelerate your success.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800"
                alt="Business Coaching"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#3498DB] rounded-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-[#F4F4F5] to-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-semibold mb-4 text-[#2C3E50]">
              What We Offer
            </h2>
            <div className="gold-line mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="service-card"
                data-testid={`feature-${index}`}
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-2xl font-semibold mb-4 text-[#2C3E50]">{feature.title}</h3>
                <p className="text-gray-700 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-semibold mb-4 text-[#2C3E50]">
              Program Benefits
            </h2>
            <div className="gold-line mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="flex items-start gap-4 p-6 bg-gradient-to-br from-white to-[#F4F4F5] rounded-xl border border-gray-200 hover:border-[#3498DB]/50 transition-all"
                data-testid={`benefit-${index}`}
              >
                <CheckCircle2 className="w-6 h-6 text-[#27AE60] flex-shrink-0 mt-1" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#2C3E50] to-[#34495E] text-white" data-testid="incubator-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold mb-6">
              Ready to Launch Your Business?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Join SAM Incubator and get the support you need to turn your vision into a thriving business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-gold ripple inline-flex items-center justify-center gap-2">
                Apply Now
                <ArrowRight size={18} />
              </Link>
              <a
                href="tel:+971563528126"
                className="btn-primary ripple inline-flex items-center justify-center gap-2"
              >
                <Phone size={18} />
                Call Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SAMIncubator;